import { EyeIcon, TargetIcon, CompassIcon } from 'lucide-react'
import type { PillarPageContent } from '../../types/pillarPage'

export const mentePage: PillarPageContent = {
  slug: 'mente',
  accent: { base: '92 91 214', deep: '30 30 61', ink: '244 242 238' },
  affiliateUrl: '',

  hero: {
    eyebrow: 'Mente · CreceElite',
    headlineTop: 'Tu vida no cambia primero afuera.',
    headlineAccent: 'Cambia primero en tu mente.',
    quote:
      '“Tu vida cambia cuando tu manera de pensar cambia. Una mente renovada abre caminos nuevos.”',
    ctaLabel: 'Renovar mi mentalidad',
    tags: ['Claridad', 'Enfoque', 'Disciplina mental', 'Crecimiento'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/ff0d4db8-4fb4-4c67-8444-a6f5bd757b2a.jpg',
    imageAlt: 'Persona sentada en silencio, concentrada, en una sala oscura iluminada desde arriba',
  },

  perspective: {
    eyebrow: 'Cambio de perspectiva',
    titleTop: 'No se trata de pensar más.',
    titleSub: 'Se trata de pensar mejor.',
    paragraphs: [
      'Puedes tener metas claras, buenas intenciones y un propósito enorme. Pero tus resultados nunca superan por mucho tiempo tu forma de pensar.',
      'Tu mente interpreta lo que vives, decide lo que haces y define lo que crees que es posible para ti.',
      'Por eso, en CreceElite no vemos el desarrollo mental como acumular información o frases motivadoras.',
    ],
    closingParagraph: 'Lo vemos como una forma de administración.',
    highlight: ['Renueva tu mente.', 'Ordena tus decisiones.', 'Abre caminos nuevos.'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1e2e8457-d74d-4945-9aa4-e701a8a7a4e6.jpg',
    imageAlt: 'Cuaderno abierto y una pluma sobre un escritorio oscuro',
    caption: 'Claridad, no ruido',
  },

  foundation: {
    eyebrow: 'La mente renovada',
    title: 'Tu mente merece atención porque de ella nace tu vida.',
    quote:
      '“No os conforméis a este siglo, sino transformaos por medio de la renovación de vuestro entendimiento.”',
    quoteSource: 'Romanos 12:2',
    lead: 'El objetivo no es tener pensamientos perfectos.',
    closing:
      'El objetivo es entrenar una mente capaz de sostener la vida que Dios puso delante de ti.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/c149f014-df3d-4f73-9ffc-f3649e566ff4.jpg',
    imageAlt: 'Persona de pie frente a una ventana alta en una habitación oscura y tranquila',
    virtues: [
      {
        label: 'Conciencia',
        text: 'Reconocer lo que piensas antes de que decida por ti.',
        icon: EyeIcon,
      },
      {
        label: 'Enfoque',
        text: 'Elegir con intención a qué le entregas tu atención cada día.',
        icon: TargetIcon,
      },
      {
        label: 'Dominio propio',
        text: 'Aprender a gobernar tus pensamientos en lugar de ser gobernado por ellos.',
        icon: CompassIcon,
      },
    ],
  },

  pillars: {
    eyebrow: 'Los 3 pilares',
    title: 'Una mente fuerte se construye sobre tres fundamentos.',
    items: [
      {
        number: '01',
        label: 'Pensamiento',
        headline: 'Lo que crees determina lo que decides.',
        text: 'Muchas de tus limitaciones no son circunstancias: son creencias que nunca revisaste. Aprender a identificarlas y reemplazarlas cambia el rumbo de tus decisiones.',
      },
      {
        number: '02',
        label: 'Enfoque',
        headline: 'Tu atención es el recurso más valioso que administras.',
        text: 'Vivimos rodeados de distracción constante. Recuperar tu capacidad de concentrarte con profundidad es lo que te permite avanzar en lo que realmente importa.',
      },
      {
        number: '03',
        label: 'Descanso mental',
        headline: 'Una mente saturada no puede crear.',
        text: 'El silencio, el descanso y los momentos sin estímulo no son tiempo perdido. Son parte del proceso que le permite a tu mente ordenar, integrar y volver con claridad.',
      },
    ],
  },

  purpose: {
    eyebrow: 'El verdadero objetivo',
    title: '¿Para qué quieres una mente fuerte?',
    reasons: [
      { label: 'Para decidir mejor', text: 'Para elegir con claridad en lugar de reaccionar.' },
      { label: 'Para tu familia', text: 'Para estar presente y responder con calma.' },
      { label: 'Para tu trabajo', text: 'Para sostener el enfoque y crear con profundidad.' },
      {
        label: 'Para tu futuro',
        text: 'Para construir sobre creencias que te impulsen y no te frenen.',
      },
    ],
    statementLead: 'No entrenas tu mente solamente para pensar distinto.',
    statementAccent: 'La entrenas para vivir distinto.',
  },

  discipline: {
    eyebrow: 'Disciplina mental',
    titleTop: 'La motivación te inspira.',
    titleAccent: 'La mentalidad te sostiene.',
    paragraphs: [
      'Habrá días en los que tu mente te dirá que no puedes.',
      'Días en los que la duda hablará más fuerte que la claridad.',
    ],
    closingParagraph:
      'Pero la transformación ocurre cuando dejas de creer automáticamente todo lo que piensas.',
    sequence: ['Pensamiento', 'Creencia', 'Decisión', 'Identidad'],
    closingLead: 'No necesitas una mente perfecta.',
    closingAccent: 'Necesitas una mente entrenada.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/b1488255-ced5-4c19-830c-846ea27ef368.jpg',
  },

  path: {
    eyebrow: 'Ahora necesitas un camino',
    title: 'Ahora necesitas un camino.',
    paragraphs: [
      'Saber que necesitas cambiar tu forma de pensar es solamente el comienzo.',
      'El siguiente paso es aprender qué hacer, cómo hacerlo y cómo mantenerlo en el tiempo.',
    ],
    steps: [
      { label: 'Mentalidad', text: 'Aprende a identificar y reemplazar tus creencias limitantes.' },
      { label: 'Enfoque', text: 'Recupera tu atención en un mundo diseñado para distraerte.' },
      { label: 'Hábitos', text: 'Construye rutinas mentales que puedas mantener.' },
      { label: 'Emociones', text: 'Aprende a gestionar lo que sientes sin negarlo.' },
      { label: 'Constancia', text: 'Convierte pequeñas decisiones en una nueva forma de pensar.' },
    ],
  },

  formation: {
    eyebrow: 'El siguiente paso',
    title: 'No tienes que descubrirlo todo solo.',
    paragraphs: [
      'Una de las mejores decisiones que puedes tomar es aprender de personas que ya han recorrido este camino.',
      'Por eso, hemos seleccionado una formación que puede ayudarte a renovar tu forma de pensar, recuperar tu enfoque y avanzar hacia una versión más clara y disciplinada de ti mismo.',
    ],
    cardEyebrow: 'Formación recomendada',
    cardTitle: 'Desarrolla tu mente',
    cardQuote: '“Aprende. Piensa. Decide. Repite.”',
    highlights: ['Aprende.', 'Piensa.', 'Decide.', 'Repite.'],
    ctaLabel: 'Conocer el método',
    note: 'Una recomendación de CreceElite para quienes quieren claridad, no atajos.',
  },

  identity: {
    eyebrow: 'Identidad',
    title: '¿Quién estás decidiendo ser?',
    before: [
      'Ruido mental',
      'Dudas constantes',
      'Distracción',
      'Reaccionar',
      'Depender del ánimo',
    ],
    after: ['Claridad', 'Enfoque', 'Creencias sólidas', 'Respuesta consciente', 'Dominio propio'],
    statementLead: 'La transformación comienza mucho antes de que cambie tu vida.',
    statementAccent: 'Comienza cuando cambia tu manera de pensar.',
  },

  manifesto: {
    eyebrow: 'Manifiesto CreceElite',
    title: 'Mi mente es mi responsabilidad',
    lines: [
      'No creo todo lo que pienso. Elijo lo que alimento.',
      'No busco perfección. Busco claridad.',
      'No huyo de mis pensamientos. Los ordeno.',
      'No dependo del ánimo. Construyo hábitos mentales.',
      'No busco solamente saber más. Quiero pensar mejor.',
      'Porque todavía tengo mucho por aprender. Mucho por decidir. Mucho por construir.',
    ],
  },

  finalCta: {
    headlineTop: 'Tu mente es el punto de partida.',
    headlineAccent: 'Tu propósito es el destino.',
    paragraph:
      'Ahora que entiendes el peso que tiene tu forma de pensar, el siguiente paso es desarrollar un sistema mental que puedas llevar a la práctica.',
    ctaLabel: 'Comenzar mi camino',
    subnote: 'Accede a la formación recomendada por CreceElite.',
  },

  stickyCtaLabel: 'Comenzar mi camino',
}
